import { signOut, updateCurrentUser, type User } from "firebase/auth";
import { reportDeliberateSignOutFailed } from "../../../../shared/errorReporting";
import { safeStorage, type SafeStorageArea } from "../../../../shared/lib/safeStorage";
import { getFirebaseAuth } from "../lib/firebase";

/**
 * THE PERSON PRESSED SIGN OUT (Web Director, 2026-10-07). Different from a
 * session end (auth/signOutSafely.ts): the person means to leave a shared
 * device, and the risk is that they walk away believing they are signed out
 * when they are not. So:
 *
 * 1. Nothing moves until Firebase answers. On success the caller routes and
 *    says "You're signed out." (Product: only after it succeeded).
 * 2. Refused (Firebase Auth's IndexedDB persistence refuses writes while it
 *    is closing, and storage pressure can refuse them too): store a per-tab
 *    marker and do ONE hard reload to where the person was going. The next
 *    load finishes the sign-out BEFORE it resolves the account
 *    (`takePendingSignOut`, IdentityProvider), with a fresh SDK.
 * 3. Refused again after that reload, or no storage to hold the marker: the
 *    person STAYS SIGNED IN, honestly. The footer still says "Signed in as"
 *    with Sign out beside it, no line claims otherwise, there is no second
 *    reload, and one fixed warning is reported.
 *
 * The marker holds a state and a time and nothing else: no uid, no email, no
 * path. "1:<epoch ms>" is a refused sign-out waiting for a load that can
 * finish it; "2:<epoch ms>" is a finished one watching for Firebase to
 * restore the user from storage that refused (QE-SO-3). Both lapse after
 * MARKER_TTL_MS, and a finished one is dropped by the first load that finds
 * no stored user, so a later sign-in from another tab is never signed out by
 * this tab's reload (review P2, Web Director 2026-10-07).
 *
 * STAYING SIGNED IN MEANS PUTTING THEM BACK. Firebase Auth 1.13 drops the
 * user from memory BEFORE the persistence write it then refuses, and tells
 * no listener: the page still shows them, storage still holds them, and
 * every request would go out unsigned. `keepSignedIn` sets the same user
 * back (`updateCurrentUser` also sets memory first), so "Signed in as" is
 * true for this page as well as the next.
 */
function keepSignedIn(user: User | null): Promise<void> {
  if (!user) return Promise.resolve();
  return updateCurrentUser(getFirebaseAuth(), user).catch(() => {});
}

export const DELIBERATE_SIGN_OUT_MARKER = "lura.book.deliberate-signout-pending";

/** How long a marker is honoured. */
export const MARKER_TTL_MS = 15 * 60 * 1000;

export type PendingSignOut = "unfinished" | "finished";

function parseMarker(value: string | null, now: number): PendingSignOut | null {
  const match = value === null ? null : /^([12]):(\d{1,15})$/.exec(value);
  if (!match) return null;
  const at = Number(match[2]);
  if (!(at <= now && now - at < MARKER_TTL_MS)) return null;
  return match[1] === "1" ? "unfinished" : "finished";
}

export type DeliberateSignOutOutcome = "signed-out" | "reloading" | "stayed-signed-in";

export interface DeliberateSignOutOptions {
  /** Same-origin path the person is going to: the reload's destination. */
  target: string;
  /** Firebase signed them out: say so and route. */
  onSignedOut(): void;
  // Injected by tests.
  win?: Window;
  storage?: SafeStorageArea;
  signOutNow?(): Promise<void>;
  report?(): void;
  /** Put the signed-in user back after a refusal (keepSignedIn). */
  restore?(): Promise<void>;
  now?(): number;
}

export async function signOutDeliberately(options: DeliberateSignOutOptions): Promise<DeliberateSignOutOutcome> {
  const storage = options.storage ?? safeStorage.session;
  const signOutNow = options.signOutNow ?? (() => signOut(getFirebaseAuth()));
  const before = options.restore ? null : getFirebaseAuth().currentUser;
  const restore = options.restore ?? (() => keepSignedIn(before));
  try {
    await signOutNow();
  } catch {
    // Only once the marker is stored: without it the next load could not
    // finish the sign-out, and the reload would only hide the failure.
    if (storage.set(DELIBERATE_SIGN_OUT_MARKER, `1:${(options.now ?? Date.now)()}`)) {
      (options.win ?? window).location.replace(options.target);
      return "reloading";
    }
    await restore();
    (options.report ?? reportDeliberateSignOutFailed)();
    return "stayed-signed-in";
  }
  storage.remove(DELIBERATE_SIGN_OUT_MARKER);
  options.onSignedOut();
  return "signed-out";
}

/**
 * On load: the sign-out this tab left behind, if any is still current. A
 * malformed or lapsed marker is removed as it is read.
 */
export function pendingSignOut(
  storage: SafeStorageArea = safeStorage.session,
  now: number = Date.now()
): PendingSignOut | null {
  const raw = storage.get(DELIBERATE_SIGN_OUT_MARKER);
  const pending = parseMarker(raw, now);
  if (pending === null && raw !== null) storage.remove(DELIBERATE_SIGN_OUT_MARKER);
  return pending;
}

/** Finished: kept, briefly, to catch Firebase restoring the user (QE-SO-3). */
export function markSignOutFinished(storage: SafeStorageArea = safeStorage.session, now: number = Date.now()): void {
  storage.set(DELIBERATE_SIGN_OUT_MARKER, `2:${now}`);
}

/**
 * Taken once it is acted on (finished or refused again), and by a genuine
 * sign-in on the page: the person who signs in now is not the one leaving.
 * Never by a load that cannot see the user (unreadable storage): the next
 * load that does see them finishes the sign-out (QE-SO-3).
 */
export function clearPendingSignOut(storage: SafeStorageArea = safeStorage.session): void {
  storage.remove(DELIBERATE_SIGN_OUT_MARKER);
}

/**
 * Finish it: true when Firebase signed them out. Refused: `user` is put back
 * (keepSignedIn), one warning is reported, and the answer is false.
 */
export async function finishPendingSignOut(
  user: User,
  signOutNow: () => Promise<void> = () => signOut(getFirebaseAuth()),
  report: () => void = reportDeliberateSignOutFailed
): Promise<boolean> {
  try {
    await signOutNow();
    return true;
  } catch {
    await keepSignedIn(user);
    report();
    return false;
  }
}
